/**
 * Wave 3 Block 3A — StateTransitionModal
 * 
 * Modal dialog for transitioning authorization between workflow states.
 * Shows valid transitions and requires reason.
 * 
 * Item 3a: STS Scenarios History
 */
import { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';

// Workflow states with colors
const WORKFLOW_STATES = {
  draft: { label: { cs: 'Koncept', en: 'Draft' }, color: 'bg-gray-100 text-gray-700' },
  in_review: { label: { cs: 'V přezkoumání', en: 'In Review' }, color: 'bg-blue-100 text-blue-700' },
  submitted_to_ucl: { label: { cs: 'Odesláno na ÚCL', en: 'Submitted to ÚCL' }, color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: { cs: 'Schváleno', en: 'Approved' }, color: 'bg-green-100 text-green-700' },
  needs_amendment: { label: { cs: 'Vyžaduje úpravy', en: 'Needs Amendment' }, color: 'bg-orange-100 text-orange-700' },
  rejected: { label: { cs: 'Zamítnuto', en: 'Rejected' }, color: 'bg-red-100 text-red-700' },
  active: { label: { cs: 'Aktivní', en: 'Active' }, color: 'bg-emerald-100 text-emerald-700' },
  renewal_due: { label: { cs: 'K obnovení', en: 'Renewal Due' }, color: 'bg-amber-100 text-amber-700' },
  expired: { label: { cs: 'Vypršelo', en: 'Expired' }, color: 'bg-red-100 text-red-700' },
  withdrawn: { label: { cs: 'Staženo', en: 'Withdrawn' }, color: 'bg-gray-200 text-gray-600' }
};

export const StateTransitionModal = ({
  open,
  onClose,
  authorizationId,
  language = 'cs',
  onTransitionComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [workflowState, setWorkflowState] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [uclReference, setUclReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const t = {
    title: language === 'cs' ? 'Změna stavu autorizace' : 'Authorization State Transition',
    description: language === 'cs' 
      ? 'Vyberte nový stav a uveďte důvod změny'
      : 'Select new state and provide reason for change',
    currentState: language === 'cs' ? 'Aktuální stav' : 'Current State',
    targetState: language === 'cs' ? 'Cílový stav' : 'Target State',
    reason: language === 'cs' ? 'Důvod změny (povinné)' : 'Reason for change (required)',
    reasonPlaceholder: language === 'cs' 
      ? 'Vysvětlete důvod této změny stavu...'
      : 'Explain the reason for this state change...',
    comment: language === 'cs' ? 'Další komentář (volitelné)' : 'Additional comment (optional)',
    uclReference: language === 'cs' ? 'Reference ÚCL' : 'ÚCL Reference',
    cancel: language === 'cs' ? 'Zrušit' : 'Cancel',
    confirm: language === 'cs' ? 'Potvrdit změnu' : 'Confirm Change',
    success: language === 'cs' ? 'Stav úspěšně změněn' : 'State changed successfully',
    error: language === 'cs' ? 'Chyba při změně stavu' : 'Error changing state',
    noTransitions: language === 'cs' 
      ? 'Z aktuálního stavu není možné provést žádnou změnu'
      : 'No transitions available from current state',
    loading: language === 'cs' ? 'Načítám...' : 'Loading...'
  };
  
  // Fetch current workflow state
  useEffect(() => {
    if (open && authorizationId) {
      fetchWorkflowState();
    }
  }, [open, authorizationId]);
  
  const fetchWorkflowState = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/authorizations/${authorizationId}/workflow-state`);
      setWorkflowState(response.data);
      setSelectedTarget(null);
      setReason('');
      setComment('');
      setUclReference('');
    } catch (err) {
      console.error('Failed to fetch workflow state:', err);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedTarget || !reason.trim()) return;
    
    setSubmitting(true);
    
    try {
      await api.post(`/authorizations/${authorizationId}/transition`, {
        new_state: selectedTarget,
        reason: reason.trim(),
        comment: comment.trim() || null,
        ucl_reference: uclReference.trim() || null
      });
      
      toast.success(t.success);
      onTransitionComplete?.();
      onClose();
    } catch (err) {
      console.error('Failed to transition state:', err);
      const errorMsg = err.response?.data?.detail || t.error;
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };
  
  const currentStateConfig = workflowState?.current_state 
    ? WORKFLOW_STATES[workflowState.current_state] 
    : null;
  
  const targetStateConfig = selectedTarget 
    ? WORKFLOW_STATES[selectedTarget] 
    : null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="state-transition-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
            <p className="mt-2 text-gray-500">{t.loading}</p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Current state */}
            <div>
              <Label className="text-sm text-gray-500">{t.currentState}</Label>
              <div className="mt-1">
                {currentStateConfig && (
                  <Badge className={`${currentStateConfig.color} text-sm px-3 py-1`}>
                    {currentStateConfig.label[language]}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Transition arrow and target selection */}
            {workflowState?.valid_transitions?.length > 0 ? (
              <>
                <div className="flex items-center gap-2 text-gray-400">
                  <ArrowRight className="w-5 h-5" />
                  <span className="text-sm">{t.targetState}</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {workflowState.valid_transitions.map(state => {
                    const config = WORKFLOW_STATES[state];
                    const isSelected = selectedTarget === state;
                    
                    return (
                      <button
                        key={state}
                        onClick={() => setSelectedTarget(state)}
                        className={`px-3 py-2 rounded-lg border-2 transition-colors ${
                          isSelected 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-transparent hover:border-gray-300'
                        } ${config?.color || 'bg-gray-100'}`}
                        data-testid={`target-state-${state}`}
                      >
                        {config?.label[language] || state}
                      </button>
                    );
                  })}
                </div>
                
                {/* Reason input */}
                {selectedTarget && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="reason">{t.reason}</Label>
                      <Textarea
                        id="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t.reasonPlaceholder}
                        rows={3}
                        required
                        data-testid="transition-reason"
                      />
                    </div>
                    
                    {/* ÚCL Reference (optional, shown for UCL-related transitions) */}
                    {['submitted_to_ucl', 'approved', 'needs_amendment', 'rejected'].includes(selectedTarget) && (
                      <div className="space-y-2">
                        <Label htmlFor="uclRef">{t.uclReference}</Label>
                        <Input
                          id="uclRef"
                          value={uclReference}
                          onChange={(e) => setUclReference(e.target.value)}
                          placeholder="UCL-2026-..."
                          data-testid="ucl-reference"
                        />
                      </div>
                    )}
                    
                    {/* Additional comment */}
                    <div className="space-y-2">
                      <Label htmlFor="comment">{t.comment}</Label>
                      <Textarea
                        id="comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        data-testid="transition-comment"
                      />
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="py-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-gray-600">{t.noTransitions}</p>
                {workflowState?.is_terminal && (
                  <p className="text-sm text-gray-500 mt-1">
                    {language === 'cs' 
                      ? 'Toto je koncový stav.'
                      : 'This is a terminal state.'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t.cancel}
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedTarget || !reason.trim() || submitting}
            data-testid="confirm-transition-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {language === 'cs' ? 'Ukládám...' : 'Saving...'}
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                {t.confirm}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StateTransitionModal;
